<template>
  <!-- Modal para emissão de prescrição -->
  <div v-if="isVisible" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow-lg w-11/12 max-w-5xl max-h-[90vh] flex flex-col overflow-hidden">
      <!-- Cabeçalho do modal -->
      <div class="px-6 py-4 border-b dark:border-gray-700 flex-shrink-0">
        <div class="flex justify-between items-start mb-2">
          <div class="flex-1">
            <h3 class="text-lg font-bold text-gray-800 dark:text-white">
              Prescrição Médica - {{ paciente?.nome }}
            </h3>
            <p class="text-sm text-gray-600 dark:text-gray-300">
              ID: {{ paciente?.idPaciente }} | Acesso: {{ paciente?.numAcesso }} | Data: {{ formatarDataExame(paciente?.data) }}
            </p>
          </div>
          <button @click="fecharModal" class="text-gray-500 hover:text-gray-700 dark:text-white dark:hover:text-gray-300">
            <X class="h-6 w-6" />
          </button>
        </div>
      </div>

      <!-- Conteúdo do modal -->
      <div class="flex-1 overflow-hidden flex flex-col min-h-0">
        <!-- Editor de prescrição -->
        <div class="w-full p-4 flex flex-col h-full overflow-hidden">
          <div class="flex justify-between items-center mb-4 flex-shrink-0">
            <h4 class="font-bold text-gray-800 dark:text-white">Prescrição Médica</h4>
          </div>

          <!-- Conteúdo scrollável -->
          <div class="flex-1 overflow-y-auto min-h-0">
            <!-- Formulário da prescrição -->
            <div class="space-y-4">
              <!-- Informações do paciente -->
              <div class="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <h5 class="font-medium text-gray-900 dark:text-white mb-3">Informações do Paciente</h5>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span class="font-medium text-gray-700 dark:text-gray-300">Nome:</span>
                    <p class="text-gray-900 dark:text-white">{{ paciente?.nome }}</p>
                  </div>
                  <div>
                    <span class="font-medium text-gray-700 dark:text-gray-300">Data de Nascimento:</span>
                    <p class="text-gray-900 dark:text-white">{{ formatarDataNascimento(paciente?.dataNascimento) }}</p>
                  </div>
                  <div>
                    <span class="font-medium text-gray-700 dark:text-gray-300">CPF:</span>
                    <p class="text-gray-900 dark:text-white">{{ formatarCPF(paciente?.cpf) }}</p>
                  </div>
                  <div>
                    <span class="font-medium text-gray-700 dark:text-gray-300">Data da Prescrição:</span>
                    <p class="text-gray-900 dark:text-white">{{ formatarDataExame(paciente?.data) }}</p>
                  </div>
                </div>
              </div>

              <!-- Tipo de prescrição -->
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Tipo de Prescrição
                  </label>
                  <select v-model="tipoPrescricao"
                    class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                    <option value="medicamento">Medicamento</option>
                    <option value="exame">Exame</option>
                    <option value="procedimento">Procedimento</option>
                    <option value="outro">Outro</option>
                  </select>
                </div>
                
                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Validade da Prescrição
                  </label>
                  <input v-model="validadePrescricao" type="date"
                    class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                </div>
              </div>

              <!-- Botão para adicionar medicamentos comuns -->
              <div v-if="tipoPrescricao === 'medicamento' || tipoPrescricao === 'outro'" class="flex justify-between items-center">
                <h5 class="font-medium text-gray-900 dark:text-white">Medicamentos/Itens Prescritos</h5>
                <div class="flex space-x-2">

                  <button @click="adicionarMedicamento"
                    class="px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded text-sm transition-colors">
                    + Adicionar Item
                  </button>
                </div>
              </div>

              <!-- Lista de medicamentos -->
              <div v-if="tipoPrescricao === 'medicamento' || tipoPrescricao === 'outro'" class="border border-gray-300 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-700">
                <div v-if="medicamentos.length === 0" class="text-center py-8 text-gray-500 dark:text-gray-400">
                  <svg class="mx-auto h-12 w-12 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                  <p>Nenhum medicamento adicionado</p>
                  <p class="text-sm">Clique em "Adicionar Item" para começar</p>
                </div>
                
                <div v-else class="space-y-4">
                  <div v-for="(medicamento, index) in medicamentos" :key="index" 
                       class="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                    <div class="flex justify-between items-start mb-3">
                      <h6 class="font-medium text-gray-900 dark:text-white">Item {{ index + 1 }}</h6>
                      <button @click="removerMedicamento(index)"
                        class="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300">
                        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                    
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <!-- Nome do Medicamento -->
                      <div>
                        <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Nome do Medicamento/Item
                        </label>
                        <div class="relative medicamento-dropdown" :data-index="index">
                          <input 
                            v-model="medicamentos[index].nomeSearch" 
                            @input="buscarMedicamentos(index)"
                            @focus="mostrarOpcoesMedicamentos[index] = true"
                            type="text"
                            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                            placeholder="Digite para buscar medicamentos...">
                          
                          <!-- Dropdown de medicamentos -->
                          <div v-if="mostrarOpcoesMedicamentos[index] && (medicamentosEncontrados[index]?.length > 0 || (medicamentos[index] && medicamentos[index].nomeSearch && medicamentos[index].nomeSearch.length >= 3))" 
                               class="absolute z-50 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                            <div v-for="med in (medicamentosEncontrados[index] || [])" 
                                 :key="med.id"
                                 @click="selecionarMedicamento(index, med)"
                                 class="px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer text-sm">
                              <div class="font-medium">{{ med.denominacao_comum_brasileira }}</div>
                              <div class="text-xs text-gray-500">{{ med.concentracao }} - {{ med.forma_farmaceutica }}</div>
                            </div>
                            
                            <!-- Opção "OUTROS" -->
                            <div v-if="medicamentos[index] && medicamentos[index].nomeSearch && medicamentos[index].nomeSearch.length >= 3"
                                 @click="selecionarOutros(index)"
                                 class="px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer text-sm border-t border-gray-200 dark:border-gray-600">
                              <div class="font-medium text-blue-600 dark:text-blue-400">OUTROS - Digitar manualmente</div>
                              <div class="text-xs text-gray-500">Medicamento não encontrado na lista</div>
                            </div>
                          </div>
                        </div>
                        
                        <!-- Campo manual para "Outros" -->
                        <div v-if="medicamentos[index].nome === 'OUTROS'" class="mt-2">
                          <input v-model="medicamentos[index].nomeOutros" 
                            @input="converterNomeParaUppercase(index)"
                            type="text"
                            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                            placeholder="Digite o nome do medicamento">
                        </div>
                      </div>
                      
                      <!-- Apresentação -->
                      <div>
                        <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Apresentação
                        </label>
                        <div class="relative">
                          <select v-model="medicamentos[index].apresentacao"
                            @change="onApresentacaoChange(index)"
                            :disabled="medicamentos[index].nome && medicamentos[index].nome !== 'OUTROS'"
                            :class="[
                              'w-full px-3 py-2 border rounded-lg text-sm',
                              medicamentos[index].nome && medicamentos[index].nome !== 'OUTROS'
                                ? 'border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 cursor-not-allowed'
                                : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                            ]">
                            <option value="">Selecione...</option>
                            <option v-for="apresentacao in getApresentacoesParaIndex()" :key="apresentacao" :value="apresentacao">
                              {{ apresentacao }}
                            </option>
                            <option value="OUTROS">Outros</option>
                          </select>
                        </div>
                        
                        <!-- Campo manual para "Outros" -->
                        <div v-if="medicamentos[index].apresentacao === 'OUTROS'" class="mt-2">
                          <input v-model="medicamentos[index].apresentacaoOutros" type="text"
                            :disabled="medicamentos[index].nome && medicamentos[index].nome !== 'OUTROS'"
                            :class="[
                              'w-full px-3 py-2 border rounded-lg text-sm',
                              medicamentos[index].nome && medicamentos[index].nome !== 'OUTROS'
                                ? 'border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 cursor-not-allowed'
                                : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                            ]"
                            placeholder="Digite a apresentação">
                        </div>
                        
                        <!-- Info sobre campos bloqueados -->
                        <div v-if="medicamentos[index].nome && medicamentos[index].nome !== 'OUTROS'" class="mt-1">
                          <p class="text-xs text-blue-600 dark:text-blue-400">
                            <svg class="inline w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                            </svg>
                            Preenchido automaticamente com base no medicamento selecionado
                          </p>
                        </div>
                      </div>
                      
                      <!-- Dosagem -->
                      <div>
                        <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Dosagem
                        </label>
                        <div class="relative">
                          <select v-model="medicamentos[index].dosagem"
                            @change="onDosagemChange(index)"
                            :disabled="medicamentos[index].nome && medicamentos[index].nome !== 'OUTROS'"
                            :class="[
                              'w-full px-3 py-2 border rounded-lg text-sm',
                              medicamentos[index].nome && medicamentos[index].nome !== 'OUTROS'
                                ? 'border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 cursor-not-allowed'
                                : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                            ]">
                            <option value="">Selecione...</option>
                            <option v-for="dosagem in dosagensDisponiveis" :key="dosagem" :value="dosagem">
                              {{ dosagem }}
                            </option>
                            <option value="OUTROS">Outros</option>
                          </select>
                        </div>
                        
                        <!-- Campo manual para "Outros" -->
                        <div v-if="medicamentos[index].dosagem === 'OUTROS'" class="mt-2">
                          <input v-model="medicamentos[index].dosagemOutros" type="text"
                            class="mt-2"
                            :disabled="medicamentos[index].nome && medicamentos[index].nome !== 'OUTROS'"
                            :class="[
                              'w-full px-3 py-2 border rounded-lg text-sm',
                              medicamentos[index].nome && medicamentos[index].nome !== 'OUTROS'
                                ? 'border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 cursor-not-allowed'
                                : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                            ]"
                            placeholder="Digite a dosagem">
                        </div>
                        
                        <!-- Info sobre campos bloqueados -->
                        <div v-if="medicamentos[index].nome && medicamentos[index].nome !== 'OUTROS'" class="mt-1">
                          <p class="text-xs text-blue-600 dark:text-blue-400">
                            <svg class="inline w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path fill-rule="evenodd" d="M18 10a8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                            </svg>
                            Preenchido automaticamente com base no medicamento selecionado
                          </p>
                        </div>
                      </div>
                      
                      <!-- Frequência -->
                      <div>
                        <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Frequência
                        </label>
                        <div class="relative">
                          <select v-model="medicamentos[index].frequencia"
                            @change="onFrequenciaChange(index)"
                            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm">
                            <option value="">Selecione...</option>
                            <option v-for="frequencia in frequenciasDisponiveis" :key="frequencia" :value="frequencia">
                              {{ frequencia }}
                            </option>
                            <option value="OUTROS">Outros</option>
                          </select>
                        </div>
                        
                        <!-- Campo manual para "Outros" -->
                        <div v-if="medicamentos[index].frequencia === 'OUTROS'" class="mt-2">
                          <input v-model="medicamentos[index].frequenciaOutros" type="text"
                            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                            placeholder="Digite a frequência">
                        </div>
                      </div>
                      
                      <!-- Duração -->
                      <div>
                        <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Duração
                        </label>
                        <div class="relative">
                          <select v-model="medicamentos[index].duracao"
                            @change="onDuracaoChange(index)"
                            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm">
                            <option value="">Selecione...</option>
                            <option v-for="duracao in duracoesDisponiveis" :key="duracao" :value="duracao">
                              {{ duracao }}
                            </option>
                            <option value="OUTROS">Outros</option>
                          </select>
                        </div>
                        
                        <!-- Campo manual para "Outros" -->
                        <div v-if="medicamentos[index].duracao === 'OUTROS'" class="mt-2">
                          <input v-model="medicamentos[index].duracaoOutros" type="text"
                            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                            placeholder="Digite a duração">
                        </div>
                      </div>
                      
                      <!-- Quantidade -->
                      <div>
                        <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Quantidade
                        </label>
                        <div class="relative">
                          <select v-model="medicamentos[index].quantidade"
                            @change="onQuantidadeChange(index)"
                            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm">
                            <option value="">Selecione...</option>
                            <option v-for="quantidade in quantidadesDisponiveis" :key="quantidade" :value="quantidade">
                              {{ quantidade }}
                            </option>
                            <option value="OUTROS">Outros</option>
                          </select>
                        </div>
                        
                        <!-- Campo manual para "Outros" -->
                        <div v-if="medicamentos[index].quantidade === 'OUTROS'" class="mt-2">
                          <input v-model="medicamentos[index].quantidadeOutros" type="text"
                            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                            placeholder="Digite a quantidade">
                        </div>
                      </div>
                    </div>
                    
                    <div class="mt-3">
                      <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Observações
                      </label>
                      <textarea v-model="medicamentos[index].observacoes"
                        class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm resize-none"
                        rows="2"
                        placeholder="Observações específicas sobre este medicamento..."></textarea>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Seção para Exames e Procedimentos -->
              <div v-if="tipoPrescricao === 'exame' || tipoPrescricao === 'procedimento'" class="space-y-4">
                <!-- Botão para adicionar exames/procedimentos -->
                <div class="flex justify-between items-center">
                  <h5 class="font-medium text-gray-900 dark:text-white">
                    {{ tipoPrescricao === 'exame' ? 'Exames' : 'Procedimentos' }} Prescritos
                  </h5>
                  <div class="flex space-x-2">
                    <button @click="adicionarExameProcedimento"
                      class="px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded text-sm transition-colors">
                      + Adicionar {{ tipoPrescricao === 'exame' ? 'Exame' : 'Procedimento' }}
                    </button>
                  </div>
                </div>

                <!-- Lista de exames/procedimentos -->
                <div class="border border-gray-300 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-700">
                  <div v-if="examesProcedimentos.length === 0" class="text-center py-8 text-gray-500 dark:text-gray-400">
                    <svg class="mx-auto h-12 w-12 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                    <p>Nenhum {{ tipoPrescricao === 'exame' ? 'exame' : 'procedimento' }} adicionado</p>
                    <p class="text-sm">Clique em "Adicionar {{ tipoPrescricao === 'exame' ? 'Exame' : 'Procedimento' }}" para começar</p>
                  </div>
                  
                  <div v-else class="space-y-4">
                    <div v-for="(item, index) in examesProcedimentos" :key="index" 
                         class="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                      <div class="flex justify-between items-start mb-3">
                        <h6 class="font-medium text-gray-900 dark:text-white">
                          {{ tipoPrescricao === 'exame' ? 'Exame' : 'Procedimento' }} {{ index + 1 }}
                        </h6>
                        <button @click="removerExameProcedimento(index)"
                          class="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300">
                          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                      
                      <div class="grid grid-cols-1 gap-4">
                        <!-- Nome do Exame/Procedimento -->
                        <div>
                          <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Nome do {{ tipoPrescricao === 'exame' ? 'Exame' : 'Procedimento' }}
                          </label>
                          <input 
                            v-model="item.nome" 
                            type="text"
                            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                            :placeholder="`Digite o nome do ${tipoPrescricao === 'exame' ? 'exame' : 'procedimento'}...`">
                        </div>
                        
                        <!-- Observações -->
                        <div>
                          <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Observações
                          </label>
                          <textarea v-model="item.observacoes"
                            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm resize-none"
                            rows="2"
                            :placeholder="`Observações sobre o ${tipoPrescricao === 'exame' ? 'exame' : 'procedimento'}...`"></textarea>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Observações Gerais -->
              <div class="space-y-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Observações Gerais
                  </label>
                  <textarea v-model="observacoesGerais"
                    class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                    rows="3"
                    placeholder="Observações gerais sobre a prescrição..."></textarea>
                </div>
              </div>

            </div>
          </div>

          <!-- Controles - sempre visíveis -->
          <div class="flex justify-between items-center pt-4 border-t border-gray-200 dark:border-gray-700 mt-4 flex-shrink-0 bg-white dark:bg-gray-800">
            <div class="flex space-x-2">
              <button @click="imprimirPrescricao"
                :disabled="(tipoPrescricao === 'medicamento' || tipoPrescricao === 'outro') && medicamentos.length === 0 || (tipoPrescricao === 'exame' || tipoPrescricao === 'procedimento') && examesProcedimentos.length === 0"
                class="px-4 py-2 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg text-sm transition-colors">
                Imprimir
              </button>
            </div>
            
            <div class="flex space-x-2">
              <button @click="fecharModal"
                class="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors">
                Cancelar
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Banner Message -->
    <BannerMessage
      v-if="showBannerMessage"
      :type="bannerType"
      :message="bannerMessage"
      @close="showBannerMessage = false"
      style="z-index: 999999;"
    />
  </div>
</template>

<script setup>
import { ref, watch, defineProps, defineEmits, onMounted, onUnmounted } from 'vue';
import { X } from 'lucide-vue-next';
import BannerMessage from '@/components/BannerMessage.vue';
import apiClient from '@/services/authService';

// Props
const props = defineProps({
  isVisible: {
    type: Boolean,
    default: false
  },
  paciente: {
    type: Object,
    default: null
  }
});

// Emits
const emit = defineEmits(['close', 'prescricao-emitida', 'prescricao-salva']);

// Estado reativo
const tipoPrescricao = ref('medicamento');
const validadePrescricao = ref('');
const medicamentos = ref([]);
const observacoesGerais = ref('');
const prescricaoAtual = ref(null);

// Estados para exames e procedimentos
const examesProcedimentos = ref([]);

// Estados de loading
const carregandoPrescricao = ref(false);

// Banner
const showBannerMessage = ref(false);
const bannerMessage = ref('');
const bannerType = ref('error');

// Dados pré-cadastrados para opções
const apresentacoesDisponiveis = ref([]); // Será preenchido com dados do banco
const dosagensDisponiveis = ref([
  '1 comprimido', '500mg', '1000mg', '2000mg', '1 cápsula', '100ml', '50ml', '10ml'
]);
const frequenciasDisponiveis = ref([
  '6/6 horas', '8/8 horas', '1x ao dia', '2x ao dia', '3x ao dia', '4x ao dia', '5x ao dia'
]);
const duracoesDisponiveis = ref([
  '5 dias', '7 dias', '10 dias', '15 dias', '30 dias', '60 dias', '90 dias'
]);
const quantidadesDisponiveis = ref([
  '20 comprimidos', '30 cápsulas', '100ml', '50ml'
]);

// Estados para busca de medicamentos
const medicamentosEncontrados = ref({});
const mostrarOpcoesMedicamentos = ref({});

// Função para garantir que os arrays de busca estejam inicializados
function inicializarArraysBusca() {
  if (!medicamentosEncontrados.value) {
    medicamentosEncontrados.value = {};
  }
  if (!mostrarOpcoesMedicamentos.value) {
    mostrarOpcoesMedicamentos.value = {};
  }
  console.log('Arrays de busca inicializados:', {
    medicamentosEncontrados: medicamentosEncontrados.value,
    mostrarOpcoesMedicamentos: mostrarOpcoesMedicamentos.value
  });
}

// Função para carregar todas as formas farmacêuticas disponíveis
async function carregarFormasFarmaceuticas() {
  try {
    console.log('Carregando formas farmacêuticas disponíveis...');
    const response = await apiClient.get(`/api/v1/medicamentos/formas-farmaceuticas/`);
    const formas = response.data || [];
    
    console.log('Formas farmacêuticas carregadas:', formas);
    
    if (formas.length > 0) {
      // Ordenar as formas alfabeticamente
      formas.sort();
      apresentacoesDisponiveis.value = formas;
    } else {
      // Fallback para formas padrão se não houver dados no banco
      apresentacoesDisponiveis.value = [
        'Comprimido', 'Cápsula', 'Gel', 'Pomada', 'Solução', 'Injeto', 'Inalação', 'Suplemento'
      ];
    }
  } catch (error) {
    console.error('Erro ao carregar formas farmacêuticas:', error);
    // Em caso de erro, usar formas padrão
    apresentacoesDisponiveis.value = [
      'Comprimido', 'Cápsula', 'Gel', 'Pomada', 'Solução', 'Injeto', 'Inalação', 'Suplemento'
    ];
  }
}

// Função para obter apresentações para um índice específico (agora sempre retorna todas as formas)
function getApresentacoesParaIndex() {
  return apresentacoesDisponiveis.value;
}

// Watchers
watch(() => props.isVisible, async (novoValor) => {
  if (novoValor && props.paciente) {
    inicializarArraysBusca();
    await carregarDadosIniciais();
  }
});

watch(() => props.paciente, async (novoPaciente) => {
  if (novoPaciente && props.isVisible) {
    inicializarArraysBusca();
    await carregarDadosIniciais();
  }
});

// Watcher para mudanças no tipo de prescrição
watch(tipoPrescricao, (novoTipo) => {
  if (novoTipo === 'exame' || novoTipo === 'procedimento') {
    // Limpar medicamentos quando mudar para exame/procedimento
    medicamentos.value = [];
    // Adicionar um item padrão para exame/procedimento
    if (examesProcedimentos.value.length === 0) {
      adicionarExameProcedimento();
    }
  } else if (novoTipo === 'medicamento' || novoTipo === 'outro') {
    // Limpar exames/procedimentos quando mudar para medicamento/outro
    examesProcedimentos.value = [];
    // Adicionar um medicamento padrão se não houver
    if (medicamentos.value.length === 0) {
      adicionarMedicamentoPadrao();
    }
  }
});

// Lifecycle hooks
onMounted(async () => {
  inicializarArraysBusca();
  
  // Carregar formas farmacêuticas sempre que o componente for montado
  await carregarFormasFarmaceuticas();
  
  if (props.isVisible && props.paciente) {
    await carregarDadosIniciais();
  }
  
  // Definir validade padrão (30 dias)
  if (!validadePrescricao.value) {
    const dataFutura = new Date();
    dataFutura.setDate(dataFutura.getDate() + 30);
    validadePrescricao.value = dataFutura.toISOString().split('T')[0];
  }
  
  // Adicionar listener para fechar dropdowns quando clicar fora
  document.addEventListener('click', handleClickOutside);
});

// Cleanup no unmount
onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});

// Função para fechar dropdowns quando clicar fora
function handleClickOutside(event) {
  // Verificar se o clique foi fora de qualquer dropdown de medicamento
  const medicamentoDropdowns = document.querySelectorAll('.medicamento-dropdown');
  let clickedInsideDropdown = false;
  
  medicamentoDropdowns.forEach(dropdown => {
    if (dropdown.contains(event.target)) {
      clickedInsideDropdown = true;
    }
  });
  
  if (!clickedInsideDropdown) {
    // Fechar todos os dropdowns
    if (mostrarOpcoesMedicamentos.value) {
      Object.keys(mostrarOpcoesMedicamentos.value).forEach(index => {
        mostrarOpcoesMedicamentos.value[index] = false;
      });
    }
  }
}

// Métodos
async function carregarDadosIniciais() {
  if (!props.paciente) return;
  
  carregandoPrescricao.value = true;
  try {
    // Carregar formas farmacêuticas disponíveis
    await carregarFormasFarmaceuticas();
    
    // Carregar prescrição existente se houver
    
    // Se não há medicamentos, adicionar um medicamento padrão
    if (medicamentos.value.length === 0) {
      adicionarMedicamentoPadrao();
    }
    
    // Se não há exames/procedimentos, adicionar um padrão
    if (examesProcedimentos.value.length === 0) {
      adicionarExameProcedimento();
    }
    
    // Definir validade padrão (30 dias a partir de hoje)
    if (!validadePrescricao.value) {
      const dataFutura = new Date();
      dataFutura.setDate(dataFutura.getDate() + 30);
      validadePrescricao.value = dataFutura.toISOString().split('T')[0];
    }
    
  } catch (error) {
    console.error('Erro ao carregar dados iniciais:', error);
    showBanner('error', 'Erro ao carregar dados da prescrição');
  } finally {
    carregandoPrescricao.value = false;
  }
}

// Funções para gerenciar exames e procedimentos
function adicionarExameProcedimento() {
  examesProcedimentos.value.push({
    nome: '',
    observacoes: ''
  });
}

function removerExameProcedimento(index) {
  examesProcedimentos.value.splice(index, 1);
}

function adicionarMedicamento() {
  medicamentos.value.push({
    nome: '',
    nomeSearch: '',
    nomeOutros: '',
    apresentacao: '',
    apresentacaoOutros: '',
    dosagem: '',
    dosagemOutros: '',
    frequencia: '',
    frequenciaOutros: '',
    duracao: '',
    duracaoOutros: '',
    quantidade: '',
    quantidadeOutros: '',
    observacoes: ''
  });
  
  // As apresentações são carregadas automaticamente do banco de dados
}

function removerMedicamento(index) {
  // Garantir que os arrays estejam inicializados
  inicializarArraysBusca();
  
  medicamentos.value.splice(index, 1);
  
  // Reindexar os arrays de busca para manter a consistênciao
  const novosMedicamentosEncontrados = {};
  const novosMostrarOpcoesMedicamentos = {};
  
  // Reindexar medicamentosEncontrados
  Object.keys(medicamentosEncontrados.value || {}).forEach(key => {
    const keyNum = parseInt(key);
    if (keyNum < index) {
      novosMedicamentosEncontrados[key] = medicamentosEncontrados.value[key];
    } else if (keyNum > index) {
      novosMedicamentosEncontrados[keyNum - 1] = medicamentosEncontrados.value[key];
    }
  });
  
  // Reindexar mostrarOpcoesMedicamentos
  Object.keys(mostrarOpcoesMedicamentos.value || {}).forEach(key => {
    const keyNum = parseInt(key);
    if (keyNum < index) {
      novosMostrarOpcoesMedicamentos[key] = mostrarOpcoesMedicamentos.value[key];
    } else if (keyNum > index) {
      novosMostrarOpcoesMedicamentos[keyNum - 1] = mostrarOpcoesMedicamentos.value[key];
    }
  });
  
  medicamentosEncontrados.value = novosMedicamentosEncontrados;
  mostrarOpcoesMedicamentos.value = novosMostrarOpcoesMedicamentos;
}

function adicionarMedicamentoPadrao() {
  medicamentos.value.push({
    nome: '',
    nomeSearch: '',
    nomeOutros: '',
    apresentacao: '',
    apresentacaoOutros: '',
    dosagem: '',
    dosagemOutros: '',
    frequencia: '',
    frequenciaOutros: '',
    duracao: '',
    duracaoOutros: '',
    quantidade: '',
    quantidadeOutros: '',
    observacoes: ''
  });
  
  // As apresentações são carregadas automaticamente do banco de dados
}



async function buscarMedicamentos(index) {
  // Garantir que os arrays estejam inicializados
  inicializarArraysBusca();
  
  if (!medicamentos.value[index]) {
    medicamentosEncontrados.value[index] = [];
    mostrarOpcoesMedicamentos.value[index] = false;
    return;
  }
  
  const termoBusca = medicamentos.value[index].nomeSearch;
  if (!termoBusca || termoBusca.length < 3) {
    medicamentosEncontrados.value[index] = [];
    mostrarOpcoesMedicamentos.value[index] = false;
    return;
  }

  try {
    const response = await apiClient.get(`/api/v1/medicamentos/search/autocomplete?q=${encodeURIComponent(termoBusca)}&limit=10`);
    medicamentosEncontrados.value[index] = response.data || [];
    mostrarOpcoesMedicamentos.value[index] = true;
  } catch (error) {
    console.error('Erro ao buscar medicamentos:', error);
    medicamentosEncontrados.value[index] = [];
    mostrarOpcoesMedicamentos.value[index] = false;
  }
}

async function selecionarMedicamento(index, medicamento) {
  // Garantir que os arrays estejam inicializados
  inicializarArraysBusca();
  
  if (!medicamentos.value[index]) return;
  
  // Debug: Log dos dados recebidos
  console.log('Medicamento selecionado:', medicamento);
  
  // Converter o nome do medicamento para uppercase
  const nomeMedicamento = medicamento.denominacao_comum_brasileira ? medicamento.denominacao_comum_brasileira.toUpperCase() : '';
  
  medicamentos.value[index].nome = nomeMedicamento;
  medicamentos.value[index].nomeSearch = nomeMedicamento;
  medicamentos.value[index].nomeOutros = '';
  medicamentos.value[index].apresentacao = medicamento.forma_farmaceutica || '';
  medicamentos.value[index].apresentacaoOutros = '';
  medicamentos.value[index].dosagem = medicamento.concentracao || '';
  medicamentos.value[index].dosagemOutros = '';
  medicamentos.value[index].frequencia = '';
  medicamentos.value[index].frequenciaOutros = '';
  medicamentos.value[index].duracao = '';
  medicamentos.value[index].duracaoOutros = '';
  medicamentos.value[index].quantidade = '';
  medicamentos.value[index].quantidadeOutros = '';
  medicamentos.value[index].observacoes = '';
  mostrarOpcoesMedicamentos.value[index] = false;
  medicamentosEncontrados.value[index] = [];
  
  // Adicionar a dosagem do medicamento às opções disponíveis se não existir
  if (medicamento.concentracao && !dosagensDisponiveis.value.includes(medicamento.concentracao)) {
    dosagensDisponiveis.value.unshift(medicamento.concentracao);
    console.log('Dosagem adicionada às opções disponíveis:', medicamento.concentracao);
  }
  
  // Adicionar a forma farmacêutica do medicamento às opções disponíveis se não existir
  if (medicamento.forma_farmaceutica && !apresentacoesDisponiveis.value.includes(medicamento.forma_farmaceutica)) {
    apresentacoesDisponiveis.value.unshift(medicamento.forma_farmaceutica);
    console.log('Forma farmacêutica adicionada às opções disponíveis:', medicamento.forma_farmaceutica);
  }
  
  // Debug: Log dos valores após atribuição
  console.log('Valores atribuídos - Apresentação:', medicamentos.value[index].apresentacao, 'Dosagem:', medicamentos.value[index].dosagem);
  
  // As apresentações são carregadas automaticamente do banco de dados
}

function selecionarOutros(index) {
  // Garantir que os arrays estejam inicializados
  inicializarArraysBusca();
  
  if (!medicamentos.value[index]) return;
  
  medicamentos.value[index].nome = 'OUTROS';
  medicamentos.value[index].nomeSearch = '';
  medicamentos.value[index].nomeOutros = '';
  medicamentos.value[index].apresentacao = '';
  medicamentos.value[index].apresentacaoOutros = '';
  medicamentos.value[index].dosagem = '';
  medicamentos.value[index].dosagemOutros = '';
  medicamentos.value[index].frequencia = '';
  medicamentos.value[index].frequenciaOutros = '';
  medicamentos.value[index].duracao = '';
  medicamentos.value[index].duracaoOutros = '';
  medicamentos.value[index].quantidade = '';
  medicamentos.value[index].quantidadeOutros = '';
  medicamentos.value[index].observacoes = '';
  mostrarOpcoesMedicamentos.value[index] = false;
  medicamentosEncontrados.value[index] = [];
  
  // As apresentações são carregadas automaticamente do banco de dados
}

function onApresentacaoChange(index) {
  if (medicamentos.value[index] && medicamentos.value[index].apresentacao === 'OUTROS') {
    medicamentos.value[index].apresentacaoOutros = '';
  }
}

function onDosagemChange(index) {
  if (medicamentos.value[index] && medicamentos.value[index].dosagem === 'OUTROS') {
    medicamentos.value[index].dosagemOutros = '';
  }
}

function onFrequenciaChange(index) {
  if (medicamentos.value[index] && medicamentos.value[index].frequencia === 'OUTROS') {
    medicamentos.value[index].frequenciaOutros = '';
  }
}

function onDuracaoChange(index) {
  if (medicamentos.value[index] && medicamentos.value[index].duracao === 'OUTROS') {
    medicamentos.value[index].duracaoOutros = '';
  }
}

function onQuantidadeChange(index) {
  if (medicamentos.value[index] && medicamentos.value[index].quantidade === 'OUTROS') {
    medicamentos.value[index].quantidadeOutros = '';
  }
}

function converterNomeParaUppercase(index) {
  if (medicamentos.value[index] && medicamentos.value[index].nome === 'OUTROS') {
    medicamentos.value[index].nomeOutros = medicamentos.value[index].nomeOutros.toUpperCase();
  }
}


async function imprimirPrescricao() {
  // Verificar se há conteúdo para imprimir baseado no tipo de prescrição
  if (tipoPrescricao.value === 'medicamento' || tipoPrescricao.value === 'outro') {
    if (medicamentos.value.length === 0) {
      showBanner('error', 'Adicione pelo menos um medicamento para imprimir');
      return;
    }
  } else if (tipoPrescricao.value === 'exame' || tipoPrescricao.value === 'procedimento') {
    if (examesProcedimentos.value.length === 0) {
      showBanner('error', `Adicione pelo menos um ${tipoPrescricao.value === 'exame' ? 'exame' : 'procedimento'} para imprimir`);
      return;
    }
  }
  
  try {
    // Buscar dados da clínica para as logos
    let clinicData = null;
    let reportLogoBase64 = null;
    let defaultLogoBase64 = null;
    try {
      const clinicResponse = await apiClient.get('/api/v1/clinicsettings/');
      clinicData = clinicResponse.data;
      
      // Buscar logo padrão em base64
      try {
        const defaultLogoResponse = await apiClient.get('/api/v1/clinicsettings/default_logo/');
        defaultLogoBase64 = defaultLogoResponse.data.logo_base64;
      } catch (error) {
        console.warn('Logo padrão não disponível:', error);
      }
      
      // Buscar logo da saúde pública em base64
      try {
        const logoResponse = await apiClient.get('/api/v1/clinicsettings/report_logo/');
        reportLogoBase64 = logoResponse.data.logo_base64;
      } catch (error) {
        console.warn('Logo do relatório não disponível:', error);
      }
    } catch (error) {
      console.warn('Erro ao buscar dados da clínica:', error);
      clinicData = {
        nome: 'Clínica',
        logo: '/styles/public/logo.png',
        telefone: '(11) 99999-9999'
      };
    }

    const printWindow = window.open('', '_blank');
    const conteudoImpressao = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>PRESCRIÇÃO MÉDICA - ${props.paciente?.nome}</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            color: #000;
            margin: 0;
            padding: 15px;
            background: #fff;
            font-size: 9px;
          }

          .container {
            border: 1px solid #000;
            padding: 5px;
          }

          /* --- HEADER --- */
          .header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            border-bottom: 2px solid #000;
            padding-bottom: 5px;
            margin-bottom: 5px;
          }
          .header-logo {
            max-height: 40px;
          }
          .header-title {
            font-size: 14px;
            font-weight: bold;
            text-align: center;
            flex-grow: 1;
          }

          /* --- TOP INFO BOX --- */
          .id-box-container {
            display: flex;
            justify-content: flex-end;
            margin-bottom: 5px;
          }
          .id-box {
            border: 1px solid #000;
            padding: 2px 4px;
            text-align: center;
            margin-left: 10px;
          }
          .id-box .label {
            font-size: 8px;
            font-weight: bold;
            display: block;
          }
          .id-box .value {
            font-size: 11px;
            font-weight: bold;
          }
          
          /* --- FLEX-GRID LAYOUT --- */
          .flex-grid {
            border-top: 2px solid #000;
            border-bottom: 2px solid #000;
            margin-bottom: 10px;
          }
          .flex-row {
            display: flex;
            border-bottom: 1px solid #000;
          }
          .flex-row:last-child {
            border-bottom: none;
          }
          .flex-item {
            padding: 3px 5px;
            border-left: 1px solid #000;
          }
          .flex-item:first-child {
            border-left: none;
          }
          .item-label {
            font-size: 7px;
            font-weight: bold;
            color: #333;
            display: block;
            margin-bottom: 2px;
            text-transform: uppercase;
          }
          .item-value {
            font-size: 11px;
            font-weight: bold;
          }

          /* --- PRESCRIÇÃO CONTENT --- */
          .prescricao-content {
            margin-top: 15px;
            padding: 10px;
            border: 1px solid #000;
            background-color: #f9f9f9;
          }
          .prescricao-title {
            font-size: 16px;
            font-weight: bold;
            text-align: center;
            margin-bottom: 15px;
            text-transform: uppercase;
          }

          /* --- MEDICAMENTOS TABLE --- */
          .medicamentos-table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 15px;
          }
          .medicamentos-table th {
            background-color: #e0e0e0;
            border: 1px solid #000;
            padding: 4px;
            font-size: 8px;
            font-weight: bold;
            text-align: center;
          }
          .medicamentos-table td {
            border: 1px solid #000;
            padding: 4px;
            font-size: 9px;
            text-align: left;
          }

          /* --- SIGNATURE SECTION --- */
          .signature-section {
            margin-top: 30px;
            display: flex;
            justify-content: center;
          }
          .signature-box {
            text-align: center;
            width: 300px;
          }
          .signature-line {
            border-top: 1px solid #000;
            margin-bottom: 5px;
            height: 40px;
          }
          .signature-text {
            font-size: 9px;
            font-weight: bold;
          }

          /* --- PRINT CONTROLS --- */
          .no-print {
            margin-top: 30px;
            text-align: center;
          }
          .no-print button {
            margin: 0 10px;
            padding: 8px 16px;
            background-color: #007bff;
            color: white;
            border: none;
            border-radius: 4px;
            cursor: pointer;
          }
          .no-print button:hover {
            background-color: #0056b3;
          }

          @media print {
            .no-print {
              display: none;
            }
          }
        </style>
      </head>
      <body>
                 <div class="container">
                      <div class="header">
            ${defaultLogoBase64 ? `<img src="data:image/png;base64,${defaultLogoBase64}" alt="Inovamed Logo" class="header-logo" style="max-height: 25px;">` : `<img src="${getClinicLogoUrl(clinicData)}" alt="Inovamed Logo" class="header-logo" style="max-height: 25px;">`}
            <div class="header-title">ㅤㅤㅤPRESCRIÇÃO MÉDICA</div>
            <div>
                ${reportLogoBase64 ? `<img src="data:image/png;base64,${reportLogoBase64}" alt="Saúde Pública Logo" class="header-logo" style="display: block; margin-bottom: 2px; max-height: 45px;">` : '<div class="header-logo" style="display: block; margin-bottom: 2px; max-height: 45px; background-color: #f0f0f0; border: 1px solid #ccc; text-align: center; line-height: 45px; font-size: 8px; color: #666;">Logo não disponível</div>'}
                
            </div>
        </div>

          <div class="id-box-container">

            <div class="id-box">
              <span class="label">DATA PRESC</span>
              <span class="value">${formatarDataExame(props.paciente?.data)}</span>
            </div>
          </div>
          
          <div class="flex-grid">
            <div class="flex-row">
              <div class="flex-item" style="flex-basis: 85%;">
                <span class="item-label">NOME</span>
                <span class="item-value">${props.paciente?.nome || ''}</span>
              </div>
            </div>
            <div class="flex-row">
              <div class="flex-item" style="flex-basis: 50%;">
                <span class="item-label">TIPO</span>
                <span class="item-value">${tipoPrescricao.value}</span>
              </div>
              <div class="flex-item" style="flex-basis: 50%;">
                <span class="item-label">VALIDADE</span>
                <span class="item-value">${formatarDataExame(validadePrescricao.value)}</span>
              </div>
            </div>
            <div class="flex-row">
              <div class="flex-item" style="width: 100%;">
                <span class="item-label">EXAME</span>
                <span class="item-value">${props.paciente?.exame || ''}</span>
              </div>
            </div>
          </div>

          <div class="prescricao-content">
            <div class="prescricao-title">PRESCRIÇÃO MÉDICA</div>
            
            ${tipoPrescricao.value === 'medicamento' || tipoPrescricao.value === 'outro' ? `
            <table class="medicamentos-table">
              <thead>
                <tr>
                  <th>Medicamento</th>
                  <th>Dosagem</th>
                  <th>Frequência</th>
                  <th>Duração</th>
                  <th>Observações</th>
                </tr>
              </thead>
              <tbody>
                ${medicamentos.value.map(med => {
                  // Determinar o nome do medicamento
                  let nomeMedicamento = med.nome;
                  if (med.nome === 'OUTROS' && med.nomeOutros) {
                    nomeMedicamento = med.nomeOutros;
                  }
                  
                  // Determinar a dosagem
                  let dosagem = med.dosagem;
                  if (med.dosagem === 'OUTROS' && med.dosagemOutros) {
                    dosagem = med.dosagemOutros;
                  }
                  
                  // Determinar a frequência
                  let frequencia = med.frequencia;
                  if (med.frequencia === 'OUTROS' && med.frequenciaOutros) {
                    frequencia = med.frequenciaOutros;
                  }
                  
                  // Determinar a duração
                  let duracao = med.duracao;
                  if (med.duracao === 'OUTROS' && med.duracaoOutros) {
                    duracao = med.duracaoOutros;
                  }
                  
                  return `
                    <tr>
                      <td>${nomeMedicamento || ''}</td>
                      <td>${dosagem || ''}</td>
                      <td>${frequencia || ''}</td>
                      <td>${duracao || ''}</td>
                      <td>${med.observacoes || ''}</td>
                    </tr>
                  `;
                }).join('')}
              </tbody>
            </table>
            ` : `
            <table class="medicamentos-table">
              <thead>
                <tr>
                  <th>${tipoPrescricao.value === 'exame' ? 'Exame' : 'Procedimento'}</th>
                  <th>Observações</th>
                </tr>
              </thead>
              <tbody>
                ${examesProcedimentos.value.map(item => `
                  <tr>
                    <td>${item.nome || ''}</td>
                    <td>${item.observacoes || ''}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
            `}

            ${observacoesGerais.value ? `
            <div style="margin-top: 10px; padding: 5px; border: 1px solid #ccc; background-color: #f0f0f0;">
              <strong>Observações Gerais:</strong><br>
              ${observacoesGerais.value}
            </div>
            ` : ''}
          </div>

          <div class="signature-section" style="text-align: center;">
            <div class="signature-box" style="margin: 0 auto;">
              <div class="signature-line"></div>
              <div class="signature-text">Assinatura e Carimbo do Médico</div>
              
            </div>
          </div>
        </div>

        <div class="no-print">
          <button onclick="window.print()">Imprimir</button>
          <button onclick="window.close()">Fechar</button>
        </div>
      </body>
      </html>
    `;
    
    printWindow.document.write(conteudoImpressao);
    printWindow.document.close();
    printWindow.onload = function() { printWindow.print(); };
    showBanner('success', 'Prescrição enviada para impressão!');
  } catch (error) {
    console.error('Erro ao imprimir prescrição:', error);
    showBanner('error', 'Erro ao imprimir prescrição');
  }
}

function fecharModal() {
  // Limpar estados
  tipoPrescricao.value = 'medicamento';
  validadePrescricao.value = '';
  medicamentos.value = [];
  examesProcedimentos.value = [];
  observacoesGerais.value = '';
  prescricaoAtual.value = null;
  
  // Limpar dados de busca
  medicamentosEncontrados.value = {};
  mostrarOpcoesMedicamentos.value = {};
  
  // Limpar banner
  showBannerMessage.value = false;
  bannerMessage.value = '';
  bannerType.value = 'error';
  
  // Emitir evento de fechamento
  emit('close');
}

function showBanner(type, message) {
  bannerType.value = type;
  bannerMessage.value = message;
  showBannerMessage.value = true;
}

// Funções auxiliares
function formatarDataExame(dataString) {
  if (!dataString) return '';
  const date = new Date(dataString);
  return date.toLocaleDateString('pt-BR');
}

function formatarDataNascimento(dataString) {
  if (!dataString) return 'Não informado';
  const date = new Date(dataString);
  return date.toLocaleDateString('pt-BR');
}

function formatarCPF(cpf) {
  if (!cpf) return 'Não informado';
  return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
}

function getClinicLogoUrl(clinicData) {
  if (!clinicData || !clinicData.logo) {
    return '/styles/public/logo.png';
  }
  return clinicData.logo.startsWith('http') ? clinicData.logo : `http://localhost:8000${clinicData.logo}`;
}
</script>

<style scoped>
@import 'tailwindcss/base';
@import 'tailwindcss/components';
@import 'tailwindcss/utilities';

/* Estilos específicos para o modal de prescrição */
.prescricao-modal {
  z-index: 9999;
}

.prescricao-modal .modal-content {
  max-height: 90vh;
  overflow-y: auto;
}
</style> 